import React, { useState } from "react";
import { useFormik } from "formik";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { Box, Alert, Paper, IconButton, InputAdornment, Typography, Stack, Avatar, useTheme } from "@mui/material";
import { ChangeUserPass } from "../../Schemas";
import { changePassword } from "../../../utils/restAPI";
import { updateLoadingAxios } from "../../../Redux/Reducers/uiSlice";
import { useDispatch, useSelector } from "react-redux";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockIcon from "@mui/icons-material/Lock";

const fontFamily = `'Inter', 'SF Pro Display', 'Roboto', 'Arial', sans-serif`;

const ChangePassword = () => {
  const [passReset, setPassReset] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { user, userInfo } = useSelector((state) => state?.users);
  const accessToken = userInfo?.accessToken;
  const username = user?.username;
  const dispatch = useDispatch();
  const theme = useTheme();
  const formik = useFormik({
    initialValues: {
      oldPassword: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: ChangeUserPass,
    onSubmit: async ({ oldPassword, password }) => {
      dispatch(updateLoadingAxios(true));
      try {
        const response = await changePassword(
          username,
          accessToken,
          oldPassword,
          password
        );
    
        dispatch(updateLoadingAxios(false));

        if (response && response.success) {
          setPassReset(true);
          setEmailVerified(false);
        } else {
          setPassReset(false);
          setEmailVerified(true);
        }
      } catch (error) {
        setPassReset(false);
        setEmailVerified(true);
      }
    },
  });

  return (
    <Box sx={{ p: 0, maxWidth: 500, mx: "auto", marginTop: -2 }}>
      <Paper elevation={0} sx={{ p: 0, borderRadius: 2, fontFamily }}>
        {/* Header Section with Icon, Heading, and Subheading */}
        <Box sx={{ px: 4, pt: 3, pb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
              <LockIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, fontSize: 22, fontFamily, lineHeight: 1.2 }}
              >
                Change Password
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, fontFamily, mt: 0.5 }}
              >
                Update your account password to keep your account secure.
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Form Content */}
        <Box sx={{ px: 4, pb: 3 }}>
          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              label="Current Password"
              variant="outlined"
              margin="normal"
              id="oldPassword"
              name="oldPassword"
              type={showOldPassword ? "text" : "password"}
              value={formik.values.oldPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.oldPassword && Boolean(formik.errors.oldPassword)
              }
              helperText={formik.touched.oldPassword && formik.errors.oldPassword}
              sx={{ mb: 2 }}
              inputProps={{ style: { fontFamily } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      edge="end"
                    >
                      {showOldPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="New Password"
              variant="outlined"
              margin="normal"
              id="password"
              name="password"
              type={showNewPassword ? "text" : "password"}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              sx={{ mb: 2 }}
              inputProps={{ style: { fontFamily } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              variant="outlined"
              margin="normal"
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.confirmPassword &&
                Boolean(formik.errors.confirmPassword)
              }
              helperText={
                formik.touched.confirmPassword && formik.errors.confirmPassword
              }
              sx={{ mb: 3 }}
              inputProps={{ style: { fontFamily } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {passReset && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Password has been updated successfully
              </Alert>
            )}
            {emailVerified && !passReset && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to update password. Please try again.
              </Alert>
            )}

            <Button
              variant="contained"
              color="primary"
              fullWidth
              type="submit"
              disabled={formik.isSubmitting}
              sx={{
                py: 1.5,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                fontFamily,
                borderRadius: 1,
              }}
            >
              Update Password
            </Button>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default ChangePassword;
